import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import withStyles from "react-jss";
import { useHistory, useLocation, useParams } from "react-router-dom";
import COLOURS from "util/colours";
import { H2 } from "visual-components/util/texts";
import SummernoteEditor from "./summernote";

const styles = {
  container: {
    padding: 40,
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontWeight: 600,
    marginBottom: 6,
    fontSize: 14,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 6,
    fontSize: 14,
    outline: "none",
    "&:focus": {
      borderColor: "#007FFF",
      boxShadow: "0 0 3px rgba(0,123,255,0.4)",
    },
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 6,
    fontSize: 14,
  },
  dropdown: {
    padding: "10px 12px",
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 6,
    fontSize: 14,
  },
  button: {
    backgroundColor: "#007FFF",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 15,
    "&:hover": {
      backgroundColor: "#005FCC",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  loader: {
    display: "inline-block",
    width: 20,
    height: 20,
    border: "3px solid #fff",
    borderTop: "3px solid transparent",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
    marginLeft: 8,
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  error: {
    color: "red",
    marginTop: 6,
    fontSize: 13,
  },
  success: {
    color: "green",
    marginTop: 6,
    fontSize: 13,
  },
  editorContainer: {
    marginBottom: 20,
    "& .ql-container": {
      minHeight: 200, // ensure editor height
    },
  },
  previewBox: {
    border: "1px solid #ccc",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#fafafa",
    minHeight: 200,
  },
  backBtn: {
    marginTop: 20,
    backgroundColor: "#ccc",
    color: "#000",
    padding: "8px 14px",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#bbb",
    },
  },
};
function CSAdminCreateOrEditContent({ classes }) {
  const { id } = useParams();
  const history = useHistory();
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [status, setStatus] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const isEdit = Boolean(id);
  const location = useLocation();
  const isView = location.pathname.includes("/view/");
  // Fetch existing content if editing

  useEffect(() => {
    if (id) {
      const fetchContent = async () => {
        try {
          setLoading(true);
          const res = await fetch(`/cs-contents/${id}`);
          const data = await res.json();
          if (data?.content) {
            const content = data.content;
            setTitle(content.title);
            setValue(content.description);
            setStatus(content.status);
          } else {
            setError("Content not found.");
          }
        } catch (err) {
          setError("Failed to load content.");
        } finally {
          setLoading(false);
        }
      };
      fetchContent();
    }
  }, [id]);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !value.trim()) {
      setError("Title and description are required.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `/cs-contents/${id}` : `/cs-contents`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, description: value, status }),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(isEdit ? "Content updated successfully!" : "Content created successfully!");
        setTimeout(() => history.push("/cs-admin/cms"), 1500);
      } else {
        setError(data.error || "Something went wrong.");
      }
    } catch (err) {
      setError("Failed to save content.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.container} key={isView ? "view" : "edit"}>
      <H2 azure>{isView ? "View Content" : isEdit ? "Edit Content" : "Create New Content"}</H2>

      {/* Messages */}
      {error && <p className={classes.error}>{error}</p>}
      {success && <p className={classes.success}>{success}</p>}
      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className={classes.formGroup}>
          <label className={classes.label}>Title</label>
          <input
            type="text"
            value={title}
            readOnly={isView}
            onChange={(e) => setTitle(e.target.value)}
            className={classes.input}
            placeholder="Enter content title"
          />
        </div>

        {/* Description */}
        <div className={classes.formGroup}>
          <label className={classes.label}>Description</label>
          {loading ? (
            <p>Loading...</p>
          ) : isView ? (
            <div className={classes.previewBox} dangerouslySetInnerHTML={{ __html: value }} />
          ) : (
            <SummernoteEditor initialValue={value} onChange={(val) => setValue(val)} />
          )}
        </div>

        {/* Status */}
        {!isView && (
          <div className={classes.formGroup}>
            <label className={classes.label}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
              className={classes.dropdown}
            >
              <option value={0}>Draft</option>
              <option value={1}>Active</option>
              <option value={2}>Inactive</option>
              <option value={3}>Archived</option>
            </select>
          </div>
        )}

        {/* Submit */}
        {!isView && (
          <button type="submit" className={classes.button} disabled={loading}>
            {isEdit ? "Update Content" : "Create Content"}
            {loading && <span className={classes.loader}></span>}
          </button>
        )}

        {/* Back button (View Mode Only) */}
        {isView && (
          <button
            type="button"
            className={classes.backBtn}
            onClick={() => history.push("/cs-admin/cms")}
          >
            ← Back to CMS
          </button>
        )}
      </form>
    </div>
  );
}

CSAdminCreateOrEditContent.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CSAdminCreateOrEditContent);
